import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const VAPID_PUBLIC_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY;

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export function usePushNotifications() {
  const [permission, setPermission] = useState(
    typeof Notification !== 'undefined' ? Notification.permission : 'denied'
  );
  const [subscription, setSubscription] = useState(null);
  const [supported, setSupported] = useState(false);

  useEffect(() => {
    setSupported('serviceWorker' in navigator && 'PushManager' in window && !!VAPID_PUBLIC_KEY);
  }, []);

  useEffect(() => {
    if (!supported) return;
    // Check for existing subscription
    navigator.serviceWorker.ready.then(reg => {
      reg.pushManager.getSubscription().then(sub => {
        setSubscription(sub);
      });
    });
  }, [supported]);

  const subscribe = useCallback(async () => {
    if (!supported) return false;

    try {
      // Request permission
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== 'granted') return false;

      // Register service worker
      const registration = await navigator.serviceWorker.register('/sw-push.js');
      await navigator.serviceWorker.ready;

      // Subscribe to push
      const sub = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
      });

      // Send subscription to backend
      const subJson = sub.toJSON();
      await axios.post(`${API}/api/push/subscribe`, {
        endpoint: subJson.endpoint,
        keys: subJson.keys,
      });

      setSubscription(sub);
      return true;
    } catch (err) {
      console.error('[Push] Subscribe failed:', err);
      return false;
    }
  }, [supported]);

  const unsubscribe = useCallback(async () => {
    if (!subscription) return;
    try {
      const subJson = subscription.toJSON();
      await axios.delete(`${API}/api/push/unsubscribe`, {
        data: { endpoint: subJson.endpoint, keys: subJson.keys },
      });
      await subscription.unsubscribe();
      setSubscription(null);
    } catch (err) {
      console.error('[Push] Unsubscribe failed:', err);
    }
  }, [subscription]);

  const testPush = useCallback(async () => {
    try {
      await axios.post(`${API}/api/push/test`);
      return true;
    } catch {
      return false;
    }
  }, []);

  return {
    supported,
    permission,
    isSubscribed: !!subscription,
    subscribe,
    unsubscribe,
    testPush,
  };
}
